import {
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical'
import React, { useState } from 'react'
import styled from 'styled-components'
import { TaggableUser } from 'types'

const MentionChip = styled.span`
  background: ${({ theme }) => theme.forecast.primary}20;
  color: ${({ theme }) => theme.forecast.primary};
  padding: 0.125rem 0.375rem;
  border-radius: 0.75rem;
  font-weight: 500;
  font-size: 0.875rem;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0 0.125rem;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.forecast.primary}30;
  }

  &::before {
    content: '@';
    font-weight: 600;
  }
`

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
  border-radius: 0.5rem;
  padding: 1.5rem;
  max-width: 400px;
  width: 90%;
  border: 1px solid ${({ theme }) => theme.forecast.border};
`

const ModalHeader = styled.h3`
  margin: 0 0 1rem 0;
  color: ${({ theme }) => theme.forecast.defaultText};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const UserIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: ${({ theme }) => theme.forecast.primary}20;
  color: ${({ theme }) => theme.forecast.primary};
  border-radius: 50%;
  font-weight: 600;
  font-size: 1rem;
  flex-shrink: 0;
`

const ModalBody = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  margin-bottom: 1.5rem;
  line-height: 1.4;
`

const UserInfo = styled.div`
  margin-bottom: 0.75rem;
`

const InfoLabel = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const InfoValue = styled.span`
  margin-left: 0.5rem;
`

const ModalButton = styled.button`
  background: ${({ theme }) => theme.forecast.primary};
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    opacity: 0.9;
  }
`

// Helper function to convert HTML element to MentionNode
function convertMentionElement(domNode: HTMLElement): DOMConversionOutput {
  const userId = domNode.getAttribute('data-user-id') || ''
  const userName = domNode.getAttribute('data-user-name') || ''
  const userEmail = domNode.getAttribute('data-user-email') || undefined

  if (userId && userName) {
    const node = new MentionNode({
      userId,
      userName,
      userEmail,
    })
    return { node }
  }

  return { node: null }
}

export type SerializedMentionNode = Spread<
  {
    userId: string
    userName: string
    userEmail?: string
  },
  SerializedLexicalNode
>

export interface MentionPayload {
  userId: string
  userName: string
  userEmail?: string
}

export class MentionNode extends DecoratorNode<React.JSX.Element> {
  __userId: string
  __userName: string
  __userEmail?: string

  static getType(): string {
    return 'mention'
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(
      {
        userId: node.__userId,
        userName: node.__userName,
        userEmail: node.__userEmail,
      },
      node.__key
    )
  }

  constructor(payload: MentionPayload, key?: NodeKey) {
    super(key)
    this.__userId = payload.userId
    this.__userName = payload.userName
    this.__userEmail = payload.userEmail
  }

  exportJSON(): SerializedMentionNode {
    return {
      userId: this.__userId,
      userName: this.__userName,
      userEmail: this.__userEmail,
      type: 'mention',
      version: 1,
    }
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    return new MentionNode({
      userId: serializedNode.userId,
      userName: serializedNode.userName,
      userEmail: serializedNode.userEmail,
    })
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-mention')) {
          return null
        }
        return {
          conversion: convertMentionElement,
          priority: 1,
        }
      },
    }
  }

  createDOM(): HTMLElement {
    const element = document.createElement('span')
    element.setAttribute('data-lexical-mention', 'true')
    element.setAttribute('data-user-id', this.__userId)
    element.setAttribute('data-user-name', this.__userName)
    if (this.__userEmail) {
      element.setAttribute('data-user-email', this.__userEmail)
    }
    return element
  }

  updateDOM(): false {
    return false
  }

  decorate(): React.JSX.Element {
    return (
      <MentionComponent
        userId={this.__userId}
        userName={this.__userName}
        userEmail={this.__userEmail}
      />
    )
  }

  getTextContent(): string {
    return `@${this.__userName}`
  }

  isInline(): boolean {
    return true
  }

  isKeyboardSelectable(): boolean {
    return true
  }
}

interface MentionComponentProps {
  userId: string
  userName: string
  userEmail?: string
}

const MentionComponent: React.FC<MentionComponentProps> = ({
  userId,
  userName,
  userEmail,
}) => {
  const [showModal, setShowModal] = useState(false)

  const handleClick = () => {
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const getUserInitials = (name: string): string => {
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('')
  }

  return (
    <>
      <MentionChip onClick={handleClick} title={userEmail || userName}>
        {userName}
      </MentionChip>
      {showModal && (
        <Modal onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <UserIcon>{getUserInitials(userName)}</UserIcon>
              User Information
            </ModalHeader>
            <ModalBody>
              <UserInfo>
                <InfoLabel>Name:</InfoLabel>
                <InfoValue>{userName}</InfoValue>
              </UserInfo>
              {userEmail && (
                <UserInfo>
                  <InfoLabel>Email:</InfoLabel>
                  <InfoValue>{userEmail}</InfoValue>
                </UserInfo>
              )}
              <UserInfo>
                <InfoLabel>User ID:</InfoLabel>
                <InfoValue>{userId}</InfoValue>
              </UserInfo>
            </ModalBody>
            <ModalButton onClick={handleCloseModal}>Close</ModalButton>
          </ModalContent>
        </Modal>
      )}
    </>
  )
}

// Helper function to create mention nodes from user data
export const createMentionNode = (user: TaggableUser): MentionNode => {
  return new MentionNode({
    userId: user.id,
    userName: user.name,
    userEmail: user.email,
  })
}

// Function to check if a node is a mention node
export const $isMentionNode = (
  node: LexicalNode | null | undefined
): node is MentionNode => {
  return node instanceof MentionNode
}
